import { type FunctionComponent, useCallback } from "react";
import { graphql, useFragment } from "react-relay";
import { findFirstNonEmptyString } from "scmp-sdk";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { tracking } from "shared/data";
import { useCurrentPageType, useTrackImpressionEventByIntersection } from "shared/hooks";
import type { PageType } from "shared/lib/page-type";
import { getAbsoluteUrl } from "shared/lib/utils";

import { HomeV2SectionStoryBlock } from "scmp-app/components/home-v2/section-story-block";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import type { chinaFutureTechStoryPackageHomeV2Query$key } from "scmp-app/queries/__generated__/chinaFutureTechStoryPackageHomeV2Query.graphql";

import {
  Container,
  Header,
  KeyIndustries,
  KeyIndustriesLabel,
  StyledEntityOnelineMenu,
  TitleLink,
} from "./styles";

type Props = {
  className?: string;
  reference: chinaFutureTechStoryPackageHomeV2Query$key;
};

type Settings = {
  custom_text?: string;
};

export const HomeV2ChinaFutureTechStoryPackage: FunctionComponent<Props> = ({
  className,
  reference,
}) => {
  const data = useFragment(
    graphql`
      fragment chinaFutureTechStoryPackageHomeV2Query on Query
      @argumentDefinitions(
        chinaFutureTechSectionEntityId: { type: "String!" }
        chinaFutureTechStoryPackageArticlesQueueName: { type: "String!" }
      ) {
        chinaFutureTechV2Section: section(filter: { entityId: $chinaFutureTechSectionEntityId }) {
          name
          subSections {
            settings
            items {
              edges {
                ...entityOnelineMenuQueueItemsEdge
              }
            }
          }
          ...entityLink
        }
        chinaFutureTechStoryPackageV2Articles: queue(
          filter: { name: $chinaFutureTechStoryPackageArticlesQueueName }
        ) {
          settings
          items(first: 4) {
            edges {
              node {
                ... on Content {
                  entityId
                }
              }
              ...sectionStoryBlockHomeV2QueueItemsEdge
            }
          }
        }
      }
    `,
    reference,
  );

  const section = data.chinaFutureTechV2Section;
  const customText = (data.chinaFutureTechStoryPackageV2Articles?.settings as Settings)
    ?.custom_text;
  const displayName = findFirstNonEmptyString(customText, section?.name);

  const subSectionsCustomText = (section?.subSections?.settings as Settings)?.custom_text;
  const keyIndustriesLabel = findFirstNonEmptyString(subSectionsCustomText, "Key Industries:");
  const subSectionEdges = section?.subSections?.items?.edges ?? [];

  const items = data.chinaFutureTechStoryPackageV2Articles?.items?.edges ?? [];
  const listOfArticles = items.map(item => item.node.entityId).join(",");

  const currentPageType = useCurrentPageType() as
    | PageType.Homepage
    | PageType.Section
    | PageType.SubSection;

  const impressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        list_of_articles: listOfArticles,
        page_type: currentPageType,
        widget_name: "china_future_tech",
      },
      subcategory: "widget",
    }),
    [currentPageType, listOfArticles],
  );

  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: impressionGA4Event,
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });

  if (items.length === 0 || !section) return null;

  return (
    <BaseLinkContextProvider customQueryParameters={{ module: tracking.module.ChinaFutureTech }}>
      <Container className={className} ref={captureTrackImpressionEventTargetElement}>
        <HomeV2SectionStoryBlock
          header={
            <Header>
              <TitleLink reference={section}>{displayName}</TitleLink>
              {subSectionEdges.length > 0 && (
                <KeyIndustries>
                  <KeyIndustriesLabel>{keyIndustriesLabel}</KeyIndustriesLabel>
                  <StyledEntityOnelineMenu reference={subSectionEdges} />
                </KeyIndustries>
              )}
            </Header>
          }
          onClick={(entityId, urlAlias) => {
            sendGA4Tracking({
              action: "click",
              category: "recirculation",
              customized_parameters: {
                article_id: entityId,
                destination_url: getAbsoluteUrl(urlAlias),
                page_type: currentPageType,
                widget_name: "china_future_tech",
              },
              subcategory: "widget",
            });
          }}
          reference={items}
        />
      </Container>
    </BaseLinkContextProvider>
  );
};

HomeV2ChinaFutureTechStoryPackage.displayName = "HomeV2ChinaFutureTechStoryPackage";
